import api from "@/services/api";
import {
  FeedbackAnswer,
  FeedbackSurveyDefinition,
  FeedbackSurveyDraft,
} from "@/components/Core/PopupMessages/Feedback/feedbackSurvey";
import { MaybeRefOrGetter, computed, reactive, toValue } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

export const FEEDBACK_SURVEY_URL = "/feedback/survey/";

export interface FeedbackSurveyStatus {
  show: boolean;
  // The running survey, sent even when hidden so the preview can show it.
  survey: FeedbackSurveyDefinition | null;
  draft?: FeedbackSurveyDraft | null;
}

export interface FeedbackSurveySubmission {
  survey: string;
  status: "draft" | "declined" | "partial" | "completed";
  answers?: Record<string, FeedbackAnswer>;
  comment?: string;
  followUp?: boolean;
  pagePath?: string;
}

export function useFeedbackSurveyStatusQuery(
  userId: MaybeRefOrGetter<number | undefined>,
) {
  return reactive(
    useQuery({
      queryKey: ["feedbackSurvey", userId],
      queryFn: ({ signal }) =>
        api
          .get<FeedbackSurveyStatus>(FEEDBACK_SURVEY_URL, { signal })
          .then((response) => response.data),
      enabled: computed(() => toValue(userId) !== undefined),
    }),
  );
}

export function useSubmitFeedbackSurveyMutation() {
  const queryClient = useQueryClient();
  return reactive(
    // No retry: a delayed one could post under the next account's session.
    useMutation({
      mutationFn: ({
        submission,
      }: {
        userId: number;
        submission: FeedbackSurveySubmission;
      }) => api.post(FEEDBACK_SURVEY_URL, submission),
      onSuccess: (_response, variables) => {
        queryClient.setQueryData<FeedbackSurveyStatus>(
          ["feedbackSurvey", variables.userId],
          (status) => status && { ...status, show: false, draft: null },
        );
      },
    }),
  );
}

// Fire and forget: a lost draft only costs a resume.
export function useSaveFeedbackDraftMutation() {
  return reactive(
    useMutation({
      mutationFn: (submission: FeedbackSurveySubmission) =>
        api.post(FEEDBACK_SURVEY_URL, submission),
    }),
  );
}
